import React, { useEffect, useRef, useState } from "react";
import { api, getErr, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash, ArrowSquareOut, CheckCircle, ArrowUp, ArrowDown, UploadSimple, Warning, Images } from "@phosphor-icons/react";
import { FreshStartCaseStudies } from "@/components/admin/FreshStartCaseStudies";

const Card = ({ title, children, testid }) => (
  <div className="border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <div className="font-display text-base font-black tracking-tight">{title}</div>
    <div className="mt-4">{children}</div>
  </div>
);

const STATUS = {
  new: "bg-[#0044FF] text-white",
  contacted: "bg-[#F59E0B] text-white",
  won: "bg-[#22C55E] text-white",
  lost: "bg-[#9CA3AF] text-white",
  dismissed: "bg-[#F3F4F6] text-[#4B5563]",
};

export default function AdminFreshStart() {
  const [data, setData] = useState(null);
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const load = async () => {
    try {
      const { data: ov } = await api.get("/fresh-start/admin/overview");
      setData(ov);
      setC(ov.config);
    } catch (e) {
      toast.error(getErr(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/fresh-start/admin/config", c);
      setC(res.config);
      toast.success("Saved — live on /fresh-start immediately.");
    } catch (e) {
      toast.error(getErr(e));
    }
    setSaving(false);
  };

  const uploadPair = async () => {
    const b = beforeRef.current?.files?.[0];
    const a = afterRef.current?.files?.[0];
    if (!b || !a) { toast.error("Pick both a BEFORE and an AFTER photo."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("before", b);
      fd.append("after", a);
      fd.append("caption", caption);
      await api.post("/fresh-start/admin/pairs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pair published — the Before & After section is now live.");
      setCaption("");
      if (beforeRef.current) beforeRef.current.value = "";
      if (afterRef.current) afterRef.current.value = "";
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
    setUploading(false);
  };

  const deletePair = async (pair_id) => {
    if (!window.confirm("Remove this before/after pair from the page?")) return;
    try {
      await api.delete(`/fresh-start/admin/pairs/${pair_id}`);
      toast.success("Pair removed.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const movePair = async (idx, dir) => {
    const next = [...(data.pairs || [])];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setData({ ...data, pairs: next });
    try {
      await api.post("/fresh-start/admin/pairs/reorder", { order: next.map((p) => p.pair_id) });
    } catch (e) {
      toast.error(getErr(e));
      load();
    }
  };

  const updateCaption = async (pair_id, cap) => {
    try {
      await api.put(`/fresh-start/admin/pairs/${pair_id}`, { caption: cap });
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  const complete = async (id) => {
    try {
      await api.post(`/fresh-start/admin/requests/${id}/complete`);
      toast.success("Marked completed — review + photo-permission email goes out tomorrow, recurring offer at +60 days.");
      load();
    } catch (e) {
      toast.error(getErr(e));
    }
  };

  if (!data || !c) return <div className="px-5 py-10 text-sm text-[#4B5563]">Loading…</div>;
  const an = data.analytics || {};
  const reqs = data.requests || [];
  const pairs = data.pairs || [];
  const photoRate = an.form_complete ? Math.round(((an.photo_upload || 0) / an.form_complete) * 100) : 0;
  const setRow = (i, k, v) => {
    const rows = [...(c.pricing_rows || [])];
    rows[i] = { ...rows[i], [k]: v };
    setC({ ...c, pricing_rows: rows });
  };

  return (
    <div className="px-5 py-8 md:px-10 md:py-10" data-testid="admin-fresh-start-page">
      <div className="font-mono-label">Fresh Start Transformation Funnel</div>
      <h1 className="font-display text-3xl font-black tracking-tight md:text-4xl">Fresh Start</h1>
      <p className="mt-2 max-w-2xl text-sm text-[#4B5563]">
        Live at{" "}
        <a className="font-bold text-[#0044FF]" href="/fresh-start" target="_blank" rel="noreferrer">
          hcobnetwork.com/fresh-start <ArrowSquareOut className="inline" size={12} />
        </a>
        . Photo-based free estimates (no instant price). Requests land in the Quotes inbox tagged FRESH START with photos attached. Client contact is EMAIL ONLY until a client-facing A2P campaign is registered.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-6">
        {[
          ["Page views", an.page_view || 0],
          ["Form starts", an.form_start || 0],
          ["Completions", an.form_complete || 0],
          ["Photo-upload rate", `${photoRate}%`],
          ["Call clicks", an.call_click || 0],
          ["Text clicks", an.text_click || 0],
        ].map(([label, n]) => (
          <div key={label} className="border border-[#E5E7EB] bg-white p-4" data-testid={`fs-stat-${label}`}>
            <div className="font-display text-2xl font-black">{n}</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">{label}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-[#4B5563]">
        Photo-upload rate is the metric to watch — if it stays low, the photo ask on the page needs reframing.
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Pricing table (What It Costs)" testid="fs-pricing-card">
          <p className="text-sm text-[#4B5563]">Fill in junk tiers, carpet per-room rate and the bundle discount here once you have them — no redeploy.</p>
          {(c.pricing_rows || []).map((r, i) => (
            <div key={r.key} className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.6fr]">
              <Input value={r.component} onChange={(e) => setRow(i, "component", e.target.value)} data-testid={`fs-row-component-${r.key}`} />
              <Input value={r.how} onChange={(e) => setRow(i, "how", e.target.value)} data-testid={`fs-row-how-${r.key}`} />
            </div>
          ))}
          <div className="mt-4 text-xs font-bold">Starting-from figure (optional — e.g. "$650")</div>
          <Input className="mt-1 max-w-[220px]" value={c.starting_from || ""} placeholder="Leave blank until tiers exist"
            onChange={(e) => setC({ ...c, starting_from: e.target.value })} data-testid="fs-starting-from-input" />
          <div className="mt-1 text-[11px] text-[#4B5563]">Shows "Fresh Starts begin around $X" under the table. A page with no number loses people who assume they can't afford it.</div>
        </Card>

        <Card title="Trust lines" testid="fs-trust-card">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!c.show_confidentiality} data-testid="fs-toggle-confidentiality"
              onChange={(e) => setC({ ...c, show_confidentiality: e.target.checked })} />
            <span><b>"Every crew member signs a confidentiality agreement."</b><br /><span className="text-[#4B5563]">Only keep on while the Independent Contractor Agreement actually includes a confidentiality clause.</span></span>
          </label>
          <label className="mt-4 flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={!!c.show_donation} data-testid="fs-toggle-donation"
              onChange={(e) => setC({ ...c, show_donation: e.target.checked })} />
            <span><b>Donation routing sentence</b><br /><span className="text-[#4B5563]">"Donation-worthy items go to donation where possible, not straight to landfill." Turn off if there is no donation route today.</span></span>
          </label>
          <div className="mt-3 text-xs font-bold">Donation partner name (optional)</div>
          <Input className="mt-1" value={c.donation_partner || ""} placeholder="e.g. Habitat for Humanity ReStore"
            onChange={(e) => setC({ ...c, donation_partner: e.target.value })} data-testid="fs-donation-partner" />
          <div className="mt-4 border border-dashed border-[#E5E7EB] bg-[#F9FAFB] p-3 text-[11px] text-[#4B5563]">
            The words <b>licensed</b>, <b>bonded</b> and <b>insured</b> never appear on this page by design (document-integrity rule). Next flyer print: JUDGMENT-FREE • VETTED CREWS • RELIABLE • LOCAL.
          </div>
          <Button className="mt-4" disabled={saving} onClick={save} data-testid="fs-save-config">
            {saving ? "Saving…" : "Save pricing & trust lines"}
          </Button>
        </Card>

        <Card title={`Before & After pairs (${pairs.length})`} testid="fs-pairs-card">
          <p className="text-sm text-[#4B5563]">
            The section stays hidden on the page until the first pair is published. Both photos are auto-cropped to the same 4:3 frame. Shoot every pair from the identical camera position, nobody in frame on the after shot, floor fully clear. Get written permission first.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <div className="text-xs font-bold">Before photo</div>
              <input ref={beforeRef} type="file" accept="image/*" className="mt-1 block w-full text-xs" data-testid="fs-pair-before-input" />
            </div>
            <div>
              <div className="text-xs font-bold">After photo</div>
              <input ref={afterRef} type="file" accept="image/*" className="mt-1 block w-full text-xs" data-testid="fs-pair-after-input" />
            </div>
          </div>
          <Input className="mt-2" value={caption} placeholder="Caption (optional) — e.g. Living room, Towson. One day." maxLength={160}
            onChange={(e) => setCaption(e.target.value)} data-testid="fs-pair-caption-input" />
          <Button className="mt-3" onClick={uploadPair} disabled={uploading} data-testid="fs-pair-upload-btn">
            <UploadSimple size={14} /> {uploading ? "Publishing…" : "Publish pair"}
          </Button>
          {!pairs.length && (
            <div className="mt-4 border border-dashed border-[#E5E7EB] p-4 text-xs text-[#4B5563]" data-testid="fs-pairs-empty">
              No pairs yet — the Before &amp; After section is hidden on /fresh-start. One transformation reads as a lucky job; three reads as a service.
            </div>
          )}
          {!!pairs.length && (
            <div className="mt-4 space-y-3">
              {pairs.map((p, i) => (
                <div key={p.pair_id} className="border border-[#E5E7EB] p-2" data-testid={`fs-pair-tile-${p.pair_id}`}>
                  <div className="grid grid-cols-2 gap-1">
                    <img src={`${BACKEND_URL}/api/fresh-start/gallery/${p.before_id}.jpg`} alt="Before" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                    <img src={`${BACKEND_URL}/api/fresh-start/gallery/${p.after_id}.jpg`} alt="After" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  </div>
                  <Input className="mt-2 text-xs" defaultValue={p.caption} placeholder="Caption"
                    onBlur={(e) => e.target.value !== p.caption && updateCaption(p.pair_id, e.target.value)} data-testid={`fs-pair-caption-${p.pair_id}`} />
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex gap-1">
                      <button className="border border-[#E5E7EB] p-1 disabled:opacity-40" onClick={() => movePair(i, -1)} disabled={i === 0} title="Move up" data-testid={`fs-pair-up-${p.pair_id}`}><ArrowUp size={13} /></button>
                      <button className="border border-[#E5E7EB] p-1 disabled:opacity-40" onClick={() => movePair(i, 1)} disabled={i === pairs.length - 1} title="Move down" data-testid={`fs-pair-down-${p.pair_id}`}><ArrowDown size={13} /></button>
                    </div>
                    <button className="text-[#EF4444]" onClick={() => deletePair(p.pair_id)} title="Remove" data-testid={`fs-pair-delete-${p.pair_id}`}><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Follow-up automation (email only)" testid="fs-followups-card">
          <ul className="list-disc space-y-2 pl-5 text-sm text-[#4B5563]">
            <li><b>+1 day after you mark a request "Contacted"</b> in the Quotes inbox and it hasn't moved: one short, low-pressure check-in. No urgency tactics — this audience disappears under pressure.</li>
            <li><b>Day after "Mark completed"</b> below: review request + permission request for the before/after photos. Reply YES PHOTOS / NO PHOTOS.</li>
            <li><b>+60 days after completion</b>: light recurring-cleaning offer → /cleaning.</li>
          </ul>
          <div className="mt-3 text-[11px] text-[#4B5563]">Sweeper runs every 10 minutes. Requests without an email address are skipped. SMS follow-ups stay off until a client-facing A2P campaign is registered.</div>
        </Card>
      </div>

      <div className="mt-5">
        <Card title='"Real Work" case studies (anonymized)' testid="fs-case-studies-card">
          <FreshStartCaseStudies />
        </Card>
      </div>

      <div className="mt-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="font-display text-lg font-black">Estimate requests ({reqs.length} recent)</div>
          {!!data.flagged_count && (
            <span className="inline-flex items-center gap-1 bg-[#FEF2F2] px-2 py-0.5 text-[10px] font-bold text-[#B91C1C]" data-testid="fs-flagged-count">
              <Warning size={11} weight="fill" /> {data.flagged_count} flagged for review
            </span>
          )}
        </div>
        <div className="mt-3 overflow-x-auto border border-[#E5E7EB] bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#4B5563]">
                <th className="p-3">When</th><th className="p-3">Client</th><th className="p-3">Scope</th>
                <th className="p-3">Photos</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.request_id} className="border-b border-[#F3F4F6] align-top" data-testid={`fs-req-row-${r.request_id}`}>
                  <td className="p-3 text-xs text-[#4B5563]">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                  <td className="p-3">
                    <div className="font-bold">{r.name}</div>
                    <div className="text-xs text-[#4B5563]">{r.phone}{r.email ? ` · ${r.email}` : ""}</div>
                    {r.flagged && (
                      <div className="mt-1 inline-flex items-center gap-1 bg-[#FEF2F2] px-2 py-0.5 text-[10px] font-bold text-[#B91C1C]" title={(r.flag_reasons || []).join(" · ")} data-testid={`fs-req-flag-${r.request_id}`}>
                        <Warning size={10} weight="fill" /> REVIEW BEFORE DISPATCH
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    <div>{(r.service_labels || []).join(" + ")}</div>
                    <div className="text-[#4B5563]">{r.volume_label} · {r.timeline_label}</div>
                    {r.notes && <div className="mt-1 max-w-[320px] text-[#4B5563]">"{r.notes}"</div>}
                  </td>
                  <td className="p-3">
                    {(r.photos || []).length ? (
                      <div className="flex flex-wrap gap-1" data-testid={`fs-req-photos-${r.request_id}`}>
                        {r.photos.slice(0, 6).map((p) => (
                          <a key={p} href={`${BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer">
                            <img src={`${BACKEND_URL}/api/files/${p}`} alt="Client photo" className="h-12 w-12 object-cover" loading="lazy" />
                          </a>
                        ))}
                        {r.photos.length > 6 && <span className="self-center text-[11px] text-[#4B5563]">+{r.photos.length - 6}</span>}
                      </div>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#9CA3AF]"><Images size={12} /> none</span>
                    )}
                  </td>
                  <td className="p-3">
                    {r.completed_at ? (
                      <span className="bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold text-[#15803D]">COMPLETED</span>
                    ) : (
                      <span className={`px-2 py-0.5 text-[10px] font-bold tracking-widest ${STATUS[r.status] || STATUS.new}`}>{(r.status || "new").toUpperCase()}</span>
                    )}
                  </td>
                  <td className="p-3">
                    {!r.completed_at && (
                      <Button size="sm" variant="outline" onClick={() => complete(r.request_id)} data-testid={`fs-complete-${r.request_id}`}>
                        <CheckCircle size={13} /> Mark completed
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!reqs.length && <tr><td colSpan={6} className="p-6 text-center text-xs text-[#4B5563]">No estimate requests yet — the funnel is live at /fresh-start.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[11px] text-[#4B5563]">
          Status follows the Quotes inbox (New → Contacted → Won/Lost). Flagged rows mention whole-home volume or conditions covered by the cleanout stop-work protocol — scope them before dispatch.
        </div>
      </div>
    </div>
  );
}
